import { activityDatabaseService, ActivityLog as DatabaseActivityLog } from '@/services/activityDatabaseService';

export interface ActivityLog {
  id: string;
  timestamp: Date;
  type: string;
  area: string;
  message: string;
  details?: any;
  read: boolean;
}

class ActivityService {
  private static instance: ActivityService;
  private activities: ActivityLog[] = [];
  private subscribers: (() => void)[] = [];

  private constructor() {}

  public static getInstance(): ActivityService {
    if (!ActivityService.instance) {
      ActivityService.instance = new ActivityService();
    }
    return ActivityService.instance;
  }

  private notifySubscribers() {
    this.subscribers.forEach(callback => callback());
  }

  public subscribe(callback: () => void) {
    this.subscribers.push(callback);
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  public getActivities(): ActivityLog[] {
    return [...this.activities];
  }

  public getActivitiesByArea(areaId: string): ActivityLog[] {
    return this.activities.filter(activity => activity.area === areaId);
  }

  public getAlertsByArea(areaId: string): ActivityLog[] {
    return this.activities.filter(activity => activity.area === areaId && activity.type === "alert");
  }

  public getMostRecentAlertByArea(areaId: string): ActivityLog | null {
    const alerts = this.getAlertsByArea(areaId);
    if (alerts.length === 0) return null;
    return alerts.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
  }

  public async loadActivities(): Promise<void> {
    try {
      const dbActivities = await activityDatabaseService.getActivities();
      this.activities = dbActivities.map(dbActivity => ({
        id: dbActivity.id,
        timestamp: new Date(dbActivity.timestamp),
        type: dbActivity.type,
        area: dbActivity.area,
        message: dbActivity.message,
        details: dbActivity.details,
        read: dbActivity.read,
      }));
      this.notifySubscribers();
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  }

  public async addDeviceActivity(area: string, deviceName: string, action: string): Promise<void> {
    const message = `Device ${deviceName} ${action}`;
    await this.addActivity('device', area, message, { deviceName, action });
  }

  public async addMotionActivity(area: string, sensorName?: string): Promise<void> {
    const message = sensorName ? `Motion detected by ${sensorName}` : 'Motion detected';
    await this.addActivity('motion', area, message, { sensorName });
  }

  public async addDaylightActivity(area: string, luxValue: number, sensorName?: string): Promise<void> {
    const message = sensorName ? `Daylight reading: ${luxValue} lux (${sensorName})` : `Daylight reading: ${luxValue} lux`;
    await this.addActivity('daylight', area, message, { luxValue, sensorName });
  }

  public async addSceneActivity(area: string, sceneName: string, action: string): Promise<void> {
    const message = `Scene ${sceneName} ${action}`;
    await this.addActivity('scene', area, message, { sceneName, action });
  }

  public async addShadeActivity(area: string, shadeName: string, action: string): Promise<void> {
    const message = `Shade ${shadeName} ${action}`;
    await this.addActivity('shade', area, message, { shadeName, action });
  }

  public async addAlertActivity(area: string, alertName: string, action: string): Promise<void> {
    const message = `Alert ${alertName} ${action}`;
    await this.addActivity('alert', area, message, { alertName, action });
  }

  public async addFloorplanActivity(action: string): Promise<void> {
    const message = `Floorplan ${action}`;
    await this.addActivity('floorplan', 'System', message, { action });
  }

  public async addSystemActivity(message: string, area: string = 'System'): Promise<void> {
    await this.addActivity('system', area, message);
  }

  public async addEnergyActivity(area: string, message: string, details?: any): Promise<void> {
    await this.addActivity('energy', area, message, details);
  }

  public async processUIAction(action: {
    deviceId: string;
    deviceName: string;
    deviceType: string;
    areaId: string;
    actionType: string;
    actionValue?: any;
  }): Promise<void> {
    const { deviceId, deviceName, deviceType, areaId, actionType, actionValue } = action;
    
    let message = `${deviceName} was controlled`;
    let details: any = { userInitiated: true, actionType, actionValue };

    switch (actionType) {
      case "power":
        message = `${deviceName} was turned ${actionValue ? 'ON' : 'OFF'} by user`;
        break;
      case "dimmer":
        const percentage = typeof actionValue === "number" ? Math.round(actionValue * 100) : actionValue;
        message = `${deviceName} brightness set to ${percentage}% by user`;
        details.percentage = percentage;
        break;
      case "temperature":
        message = `${deviceName} temperature set to ${actionValue}K by user`;
        break;
      case "color":
        message = `${deviceName} color changed by user`;
        break;
      case "scene":
        message = `Scene "${actionValue}" activated by user`;
        break;
      default:
        message = `${deviceName} was controlled by user`;
        if (actionValue !== undefined) {
          details.value = actionValue;
        }
    }

    await this.addActivity('device', message, areaId, details);
  }

  public async addActivity(type: string, area: string, message: string, details?: any): Promise<void> {
    try {
      const activity: DatabaseActivityLog = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        type,
        area,
      message,
      details,
        read: false,
      };

      await activityDatabaseService.createActivity(activity);
      await this.loadActivities(); // Reload activities to get the latest data
    } catch (error) {
      console.error('Error adding activity:', error);
    }
  }

  public async markAsRead(id: string): Promise<void> {
    try {
      await activityDatabaseService.updateActivity(id, { read: true });
      await this.loadActivities();
    } catch (error) {
      console.error('Error marking activity as read:', error);
    }
  }

  public async deleteActivity(id: string): Promise<void> {
    try {
      await activityDatabaseService.deleteActivity(id);
      await this.loadActivities();
    } catch (error) {
      console.error('Error deleting activity:', error);
    }
  }

  public async clearAll(): Promise<void> {
    try {
      await activityDatabaseService.clearAllActivities();
      await this.loadActivities();
    } catch (error) {
      console.error('Error clearing activities:', error);
    }
  }
}

export const activityService = ActivityService.getInstance();
